import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CARGO_UNIT_STATUSES, statusLabel } from "@wms/domain";
import type { ActionField, ActionFormPreview, ActionMeta, ActionPreviewLine, ActionResult } from "@wms/domain";
import { Alert, App, Button, DatePicker, Descriptions, Form, Input, InputNumber, Modal, Select, Space, Switch, Table, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { useRef, useState } from "react";
import { api, describeError, fieldErrors } from "../api/client";
import { useAuthStore } from "../auth/store";
import { actionBody, actionFieldRules } from "./actionFields";
import { outcomeUnknown } from "./formProblem";
import { GuardedInput } from "./GuardedInput";
import { policyOfActionField, unfinishedNumber } from "./inputPolicy";
import { unitProblem } from "./LinesActionModal";
import { refreshChildLists } from "./recordQueries";
import { RefSelect } from "./RefSelect";

interface Props {
  entity: string;
  id: string;
  action: ActionMeta;
  row?: Record<string, unknown>;
  onClose: () => void;
  /** UXW-59 (option «Панель действий»): a lost answer is checked against the record before a repeat is offered. */
  verify?: boolean;
}

/** UXW-59: what the check of a lost answer found — the dialog keeps the input and the key while it checks. */
type Check = "checking" | "done" | "unchanged" | "offline";

type Unit = { name: string; decimals: number; product?: string };

function Control({ field, row, guard, unit, ...rest }: { field: ActionField; row?: Record<string, unknown>; guard?: boolean; unit?: Unit } & Record<string, unknown>) {
  // UXW-66 (option «Запрет недопустимого ввода»): a quantity of the dialog follows the same policy as in the card;
  // QA0211-B02: with the unit of its product («шт» — whole numbers, «кг» — three digits) and the unit after it
  const policy = guard ? policyOfActionField(field, unit) : null;
  if (policy) return <GuardedInput policy={policy} suffix={unit?.name} style={{ width: "100%" }} {...rest} />;
  switch (field.type) {
    case "number":
      return <InputNumber min={0} decimalSeparator="," suffix={unit?.name} style={{ width: "100%" }} {...rest} />;
    case "ref":
      // cells, zones and documents belong to a warehouse: offer only the document's own
      return (
        <RefSelect
          entity={field.ref!}
          filter={row?.warehouse_id && ["locations", "zones", "printers"].includes(field.ref!) ? { field: "warehouse_id", op: "eq", value: row.warehouse_id } : null}
          {...rest}
        />
      );
    case "enum":
      return <Select options={field.options ?? []} {...rest} />;
    case "boolean":
      // N04: a yes/no choice is a switch, never a text box that wants the word «true»
      return <Switch checkedChildren="Да" unCheckedChildren="Нет" {...rest} />;
    case "date":
      return <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} {...rest} />;
    default:
      return <Input {...rest} />;
  }
}

const fmt = (v: string) => Number(v).toLocaleString("ru-RU", { maximumFractionDigits: 3 });

/** Small form declared by the server (ActionMeta.fields); posts to /{entity}/{id}/{code} and shows what happened. */
export function FormActionModal({ entity, id, action, row, onClose, verify }: Props) {
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const guard = useAuthStore((s) => s.option)("use_input_guard");
  const [form] = Form.useForm();
  const [result, setResult] = useState<ActionResult | null>(null);
  const fields = action.fields ?? [];
  const labels = Object.fromEntries(fields.map((f) => [f.name, f.label]));
  const initial: Record<string, unknown> = {};
  for (const f of fields) {
    const value = f.default_from ? row?.[f.default_from] : undefined;
    if (value !== undefined && value !== null) initial[f.name] = f.type === "number" ? Number(value) : f.type === "date" ? dayjs(String(value)) : value;
    if (f.type === "enum" && initial[f.name] === undefined && f.required && f.options?.length) initial[f.name] = f.options[0].value;
    if (f.type === "boolean" && initial[f.name] === undefined) initial[f.name] = false;
  }

  // D08: before accepting a shortage the worker sees plan, fact and what is missing per line
  const preview = useQuery({
    queryKey: [entity, id, "preview", action.preview],
    queryFn: () => api.get<ActionFormPreview>(`/${entity}/${id}/${action.preview}`),
    enabled: !!action.preview,
  });
  const previewLines = (preview.data?.lines ?? []).filter((l) => Number(l.diff) !== 0);
  // QA0211-B02: a quantity in the unit of a product is judged at the field before «Завершить» — the same rule the
  // server checks; the value is never rounded, the button waits until it fits
  const units = preview.data?.units ?? {};
  const values = Form.useWatch([], form) as Record<string, unknown> | undefined;
  const unitProblems: Record<string, string> = {};
  for (const f of fields) {
    const unit = units[f.name];
    const raw = values?.[f.name];
    if (!unit || raw === undefined || raw === null || raw === "") continue;
    const problem = unitProblem(Number(raw), unit.product ?? "Товар", unit);
    if (problem) unitProblems[f.name] = problem;
  }
  const blocked = Object.keys(unitProblems).length > 0;

  // one key per opened dialog: a retry after a lost answer returns the first result instead of a second set (AC-06)
  const [requestKey] = useState(() => (typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`));
  const [check, setCheck] = useState<Check | null>(null);
  // the record as it was when the request left: a lost answer is judged against it (UXW-59)
  const sent = useRef<{ status: string; updated: string } | null>(null);
  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: [entity] });
    queryClient.invalidateQueries({ queryKey: ["tasks"] });
    queryClient.invalidateQueries({ queryKey: ["stock"] });
    // CW31-B11: the child tabs of this record («Оплаты» after «Зафиксировать оплату») show the new rows at once
    void refreshChildLists(queryClient, entity, id);
  };
  /** Reads the record again: changed — the action was done; the same — it was not; no answer — still unknown. */
  const verifyResult = async () => {
    setCheck("checking");
    try {
      const fresh = await api.get<Record<string, unknown>>(`/${entity}/${id}`);
      const before = sent.current;
      const changed = !!before && (String(fresh.status ?? "") !== before.status || (!!fresh.updated_at && String(fresh.updated_at) !== before.updated));
      if (changed) {
        refreshAll();
        setCheck("done");
      } else setCheck("unchanged");
    } catch {
      setCheck("offline");
    }
  };
  const submit = useMutation({
    mutationFn: (values: Record<string, unknown>) => {
      sent.current = { status: String(row?.status ?? ""), updated: String(row?.updated_at ?? "") };
      setCheck(null);
      // a date travels as «2026-10-25» (the picker holds a dayjs value); CW31-B17: a text travels trimmed;
      // a repeat after a lost answer sends the same key of this window
      const body: Record<string, unknown> = { ...actionBody(fields, values), request_key: requestKey };
      return api.post<ActionResult>(`/${entity}/${id}/${action.code}`, body);
    },
    onSuccess: (data) => {
      refreshAll();
      const rich = data && (data.next_step || data.moved || data.skipped || typeof data.labels === "number" || Array.isArray(data.codes));
      if (rich) {
        setResult(data);
        return;
      }
      message.success(data?.sscc ? `${action.label}: грузовое место ${data.sscc}` : data?.status ? `${action.label}: ${data.status_label ?? statusLabel(data.status)}` : action.label);
      onClose();
    },
    onError: (e) => {
      if (verify && outcomeUnknown(e)) {
        void verifyResult();
        return;
      }
      const errors = fieldErrors(e);
      if (errors.length) form.setFields(errors.map((x) => ({ name: x.field, errors: [x.message] })));
    },
  });

  if (result) {
    return (
      <Modal title={`${action.label}: результат`} open onCancel={onClose} footer={<Button type="primary" onClick={onClose}>Готово</Button>} destroyOnHidden width={720}>
        <Alert
          type="success"
          showIcon
          message={
            <span>
              {result.sscc ? (
                // BUG-011: a cargo unit is named by its number; each status says whose status it is
                <Space wrap size={[12, 4]}>
                  <span>
                    Грузовое место (SSCC): <Typography.Text strong copyable>{result.sscc}</Typography.Text>
                  </span>
                  {result.cargo_unit_status ? (
                    <span>
                      Статус места: <Tag color="blue">{CARGO_UNIT_STATUSES[result.cargo_unit_status] ?? statusLabel(result.cargo_unit_status)}</Tag>
                    </span>
                  ) : null}
                  {result.order_status ? (
                    <span>
                      Статус заказа: <Tag color="blue">{statusLabel(result.order_status)}</Tag>
                    </span>
                  ) : null}
                </Space>
              ) : result.status ? (
                <>
                  Статус: <Tag color="blue">{statusLabel(result.status)}</Tag>
                </>
              ) : null}
              {typeof result.labels === "number" ? `этикеток в очереди печати: ${result.labels} (заданий: ${result.jobs ?? 0})` : null}
            </span>
          }
          description={result.next_step ?? undefined}
          style={{ marginBottom: 12 }}
        />
        {result.moved && result.moved.length > 0 && (
          <Table
            size="small"
            rowKey="task"
            pagination={false}
            dataSource={result.moved}
            columns={[
              { title: "Задача отбора", dataIndex: "task" },
              { title: "Откуда", dataIndex: "from" },
              { title: "Перемещено", dataIndex: "quantity", width: 120 },
            ]}
          />
        )}
        {Array.isArray(result.codes) && result.codes.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <Typography.Text strong>{result.repeated ? "Повтор запроса: возвращены ранее созданные коды" : `Создано кодов: ${result.codes.length}`}</Typography.Text>
            <Input.TextArea readOnly rows={Math.min(8, result.codes.length + 1)} value={result.codes.join("\n")} style={{ marginTop: 6, fontFamily: "monospace" }} />
          </div>
        )}
        {result.skipped && result.skipped.length > 0 && (
          <Alert type="warning" showIcon style={{ marginTop: 12 }} message={`Пропущено: ${result.skipped.map((s) => `${s.task} — ${s.reason}`).join("; ")}`} />
        )}
      </Modal>
    );
  }

  if (check === "done") {
    // UXW-59: the record changed while the answer was lost — the action is done, nothing is offered again
    return (
      <Modal title={`${action.label}: результат`} open onCancel={onClose} footer={<Button type="primary" onClick={onClose}>Готово</Button>} destroyOnHidden>
        <Alert
          type="success"
          showIcon
          data-testid="action-verified"
          message="Действие выполнено"
          description="Ответ сервера не дошёл, но документ уже изменился — повторять не нужно. Карточка и связанные списки обновлены."
        />
      </Modal>
    );
  }

  return (
    <Modal
      title={action.label}
      open
      onCancel={onClose}
      okText={check === "offline" ? "Проверить результат" : action.label}
      cancelText="Отмена"
      confirmLoading={submit.isPending || check === "checking"}
      onOk={() => (check === "offline" ? void verifyResult() : form.submit())}
      okButtonProps={{ disabled: blocked && check !== "offline" }}
      destroyOnHidden
    >
      {preview.data?.context?.length ? (
        // QA0211-B02: what the dialog confirms — the task, the product, the batch, the plan in its unit, the cells
        <div data-testid="action-context" style={{ marginBottom: 12 }}>
          {preview.data.help ? <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>{preview.data.help}</Typography.Paragraph> : null}
          <Descriptions size="small" column={1} bordered items={preview.data.context.map((r) => ({ key: r.label, label: r.label, children: r.value }))} />
          {preview.data.unit_note ? <Typography.Text type="secondary" style={{ display: "block", marginTop: 6, fontSize: 13 }}>{preview.data.unit_note}</Typography.Text> : null}
        </div>
      ) : null}
      {action.preview && previewLines.length > 0 && (
        <>
          <Alert type="warning" showIcon style={{ marginBottom: 12 }} message="Принято не всё. Расхождение и причина останутся в истории документа." />
          <Table
            size="small"
            rowKey="line_no"
            pagination={false}
            style={{ marginBottom: 16 }}
            dataSource={previewLines}
            columns={[
              { title: "№", dataIndex: "line_no", width: 50 },
              { title: "Товар", render: (_: unknown, r: ActionPreviewLine) => `${r.storage_object_code} — ${r.storage_object_name}` },
              { title: "План", dataIndex: "plan", width: 80, align: "right" as const, render: (v: string) => fmt(v) },
              { title: "Факт", dataIndex: "fact", width: 80, align: "right" as const, render: (v: string) => fmt(v) },
              {
                title: "Расхождение",
                dataIndex: "diff",
                width: 120,
                align: "right" as const,
                render: (v: string) => <Typography.Text type={Number(v) < 0 ? "danger" : "warning"}>{Number(v) < 0 ? `не хватает ${fmt(String(-Number(v)))}` : `лишнее ${fmt(v)}`}</Typography.Text>,
              },
            ]}
          />
        </>
      )}
      <Form form={form} layout="vertical" disabled={false} initialValues={initial} onFinish={(values) => submit.mutate(values)} onValuesChange={(changed) => form.setFields(Object.keys(changed).map((name) => ({ name, errors: [] })))}>
        {fields.map((f) => (
          <Form.Item
            key={f.name}
            name={f.name}
            label={units[f.name] ? `${f.label}, ${units[f.name].name}` : f.label}
            valuePropName={f.type === "boolean" ? "checked" : "value"}
            {...(unitProblems[f.name] ? { validateStatus: "error" as const, help: <span data-testid={`field-problem-${f.name}`}>{unitProblems[f.name]}</span> } : {})}
            // CW31-B17: a required text of only spaces is not filled; numbers, switches and lists keep «required»;
            // UXW-63: with the guard an unfinished number («1,») is asked to be finished before the action is posted
            rules={
              guard && f.type === "number"
                ? [...(actionFieldRules(f) ?? []), { validator: (_: unknown, value: unknown) => (unfinishedNumber(value) ? Promise.reject(new Error(unfinishedNumber(value)!)) : Promise.resolve()) }]
                : actionFieldRules(f)
            }
          >
            <Control field={f} row={row} guard={guard} unit={units[f.name]} />
          </Form.Item>
        ))}
      </Form>
      {check === "checking" && <Alert type="info" showIcon data-testid="action-checking" message="Ответ сервера не получен. Проверяем результат…" />}
      {check === "unchanged" && (
        <Alert
          type="warning"
          showIcon
          data-testid="action-unchanged"
          message="Действие не выполнено: документ не изменился"
          description={`Ответ сервера не дошёл, и документ остался прежним. Введённое сохранено; нажмите «${action.label}» ещё раз — запрос уйдёт с тем же ключом повтора.`}
        />
      )}
      {check === "offline" && (
        <Alert
          type="warning"
          showIcon
          data-testid="action-unknown"
          message="Результат пока неизвестен: нет связи с сервером"
          description="Действие могло выполниться. Не повторяйте его: когда связь появится, нажмите «Проверить результат» — окно и введённое сохранены."
        />
      )}
      {submit.error && !check && (
        <Space direction="vertical" style={{ width: "100%" }}>
          <Alert type="error" message={describeError(submit.error, labels)} showIcon />
        </Space>
      )}
    </Modal>
  );
}
